import { z } from 'zod';
import { ArtifactReviewStatusSchema } from './credential.schema.js';
/** Renewable compliance items — periodic re-upload expected.
 *  TB_TEST (annual), VACCINATION (boosters), GENERAL_LIABILITY_INSURANCE
 *  (annual renewal), WSIB_CLEARANCE (quarterly renewal). */
export const ComplianceArtifactTypeSchema = z.enum([
    'TB_TEST',
    'VACCINATION',
    'GENERAL_LIABILITY_INSURANCE',
    'WSIB_CLEARANCE',
]);
const AllowedUploadContentTypeSchema = z
    .string()
    .regex(/^(image\/(jpeg|png|webp)|application\/pdf)$/, 'Only JPEG, PNG, WebP, or PDF allowed');
const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;
/** Two-step upload, same pattern as credentials. */
export const ComplianceArtifactUploadUrlRequestSchema = z.object({
    type: ComplianceArtifactTypeSchema,
    contentType: AllowedUploadContentTypeSchema,
    contentLength: z.number().int().positive().max(MAX_UPLOAD_BYTES),
});
export const ComplianceArtifactUploadUrlResponseSchema = z.object({
    uploadUrl: z.string().url(),
    objectStorageKey: z.string().min(1).max(500),
    expiresAt: z.string().datetime(),
});
/** Record an uploaded compliance artifact. `expiresAt` is meaningful here
 *  (unlike for most credentials): the Phase 13/15 batch job uses it to
 *  flip status to EXPIRED + emit expiry alerts. */
export const ComplianceArtifactRecordRequestSchema = z.object({
    type: ComplianceArtifactTypeSchema,
    externalReferenceNumber: z.string().max(120).trim().nullable().optional(),
    issuedAt: z.string().date().nullable().optional(),
    expiresAt: z.string().date().nullable().optional(),
    objectStorageKey: z.string().min(1).max(500),
});
export const ComplianceArtifactResponseSchema = z.object({
    id: z.string().uuid(),
    type: ComplianceArtifactTypeSchema,
    status: ArtifactReviewStatusSchema,
    externalReferenceNumber: z.string().nullable(),
    issuedAt: z.string().date().nullable(),
    expiresAt: z.string().date().nullable(),
    uploadedAt: z.string().datetime().nullable(),
    notes: z.string().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/** Phase 5f-c + 5f-2 — catalog item shape for the data-driven
 *  compliance section in the caregiver portal. Mirrors the
 *  CredentialCatalogItemSchema shape so the shared `<ArtifactRow>`
 *  consumes both without per-section branching.
 *
 *  Change-list 2.3: the catalog carries required + optional items.
 *  `requiredForCaregiver` is `true` for TB / vaccination (gating) and
 *  `false` for general liability insurance (shown + uploadable, but
 *  never blocks activation). WSIB clearance is hidden — it appears in
 *  neither list (the enum + admin path stay in the system, so it's
 *  reversible). The `<ArtifactRow>` renders a "required" / "optional"
 *  marker from this flag.
 *
 *  Per 5f-7 (the WSIB-number migration from 15-G):
 *  WSIB_CLEARANCE carries `extraFields: ['externalReferenceNumber']` in
 *  COMPLIANCE_EXTRA_FIELDS — retained for when WSIB is re-surfaced or
 *  keyed by an operator, even though it's not currently in the catalog. */
export const ComplianceArtifactCatalogItemSchema = z.object({
    type: ComplianceArtifactTypeSchema,
    /** User-facing label rendered above the upload button. */
    label: z.string(),
    /** `true` for required types (TB, vaccination), `false` for optional
     *  ones (general liability insurance) per change-list 2.3. Drives the
     *  "required"/"optional" marker in `<ArtifactRow>`. */
    requiredForCaregiver: z.boolean(),
    /** Most-recent compliance row of this type, if any. Status +
     *  expiresAt + externalReferenceNumber drive the row UX. */
    currentArtifact: z
        .object({
        id: z.string().uuid(),
        status: ArtifactReviewStatusSchema,
        expiresAt: z.string().date().nullable(),
        externalReferenceNumber: z.string().nullable(),
        /** Audit H15 (2026-09-01) — the operator-supplied reason when status is
         *  REJECTED, so the caregiver gets actionable feedback (mirrors the
         *  eftRejectionReason banking precedent). Null unless REJECTED. */
        rejectionNotes: z.string().nullable(),
    })
        .nullable(),
    /** Per-type extra input fields. WSIB_CLEARANCE has
     *  `['externalReferenceNumber']` per 5f-7. Others are bare. */
    extraFields: z.array(z.string()),
});
export const ComplianceArtifactCatalogResponseSchema = z.object({
    items: z.array(ComplianceArtifactCatalogItemSchema),
});
//# sourceMappingURL=compliance.schema.js.map